import { Box } from '@material-ui/core'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import * as Sentry from '@sentry/react'
import { ClosableDialog } from 'components/controls/dialogs'
import { CircularLoading } from 'components/facets'
import { useUser } from 'components/providers/UserProvider'
import { publishChangesToPTInventory } from 'lib/api'
import { useErrorHandler } from 'lib/hooks'
import { useStockStore } from 'lib/hooks/useStockStore'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DATA_PREPARATION_ERROR,
  INVALID_PRICE,
  InventoryArticle,
  INVENTORY_TRANSACTION_CAUSES,
  PublishChangesResponse,
  toSlimInventoryArticleList,
} from 'shared'
import { PublishChangesSummary } from './PublishChangesSummary'

interface PublishChangesDialogProps {
  articles: InventoryArticle[]
  handleChangesPublished: (failed: PublishChangesResponse) => void
  open: boolean
  setOpen: (open: boolean) => void
  startPolling: (idGame: number) => void
}

export const PublishChangesDialog = ({
  open,
  articles,
  setOpen,
  handleChangesPublished,
  startPolling,
}: PublishChangesDialogProps): JSX.Element => {
  const [publishResults, setPublishResults] = useState<PublishChangesResponse | undefined>(
    undefined
  )
  const [loading, setLoading] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const { handle } = useErrorHandler()
  const { setStockLock } = useStockStore()

  const { user, activeGame } = useUser()
  const { t } = useTranslation()
  // const { handle } = useErrorHandler()

  const handleClose = (): void => {
    setPublishResults(undefined)
    setOpen(false)
  }
  publishResults

  const getArticlesChanged = (articles: InventoryArticle[]): InventoryArticle[] => {
    const articlesChanged: InventoryArticle[] = []
    for (let i = 0; i < articles.length; i++) {
      if (
        articles[i].newQuantity ||
        articles[i].newPrice !== articles[i].price ||
        articles[i].newComment ||
        articles[i].newCondition
      ) {
        articlesChanged.push(articles[i])
      }
    }
    return articlesChanged
  }
  const articlesChanged = getArticlesChanged(articles)

  const handlePublish = async (): Promise<void> => {
    setLoading(true)

    try {
      const results = await publishChangesToPTInventory(
        user,
        toSlimInventoryArticleList(articlesChanged),
        false,
        INVENTORY_TRANSACTION_CAUSES.MANUAL_CHANGE
      )

      // Response OK (status 200)
      setPublishResults(results)

      handleChangesPublished(results)
      setStockLock(true)
      await startPolling(activeGame.idGame)
      handleClose()
    } catch (err: any) {
      Sentry.withScope((scope) => {
        scope.setTag('component', 'SaveToInventoryDialog')
        scope.setExtra('context', 'publishChangesToPTInventory')
        scope.setExtra('articles', toSlimInventoryArticleList(articlesChanged))
        Sentry.captureException(err)
      })

      const status = err?.response?.status

      const backendMessage = err?.response?.data?.message

      if (status === 422) {
        if (backendMessage === INVALID_PRICE) {
          enqueueSnackbar(`${t('error.invalidPrice')}`, { variant: 'error' })
        } else if (backendMessage === DATA_PREPARATION_ERROR) {
          enqueueSnackbar(`${t('error.cannotSaveArticles')}`, { variant: 'error' })
        }
      } else if (status === 504) {
        enqueueSnackbar(`${t('error.gatewayTimeoutInventoryError')}`, { variant: 'error' })
      } else {
        handle(err)
      }
    }

    setLoading(false)
  }

  return (
    <ClosableDialog
      open={open}
      maxWidth="lg"
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
      hideButton={true}
    >
      <Box width="400px" maxWidth="100%">
        <DialogTitle disableTypography id="form-dialog-title">
          <h3>{t('stockPricing.actions.publishChangesPT')}</h3>
        </DialogTitle>
      </Box>

      <Box>
        <DialogContent>
          {loading ? (
            <Box display="flex" padding={3} justifyContent="center">
              <CircularLoading />
            </Box>
          ) : (
            <PublishChangesSummary
              articlesChanged={articlesChanged}
              onPublishChanges={handlePublish}
              onCancelClick={handleClose}
            />
          )}
        </DialogContent>
      </Box>
    </ClosableDialog>
  )
}
